import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Search, Save, ShieldAlert, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminWeeklyPicks = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [memberQ, setMemberQ] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [memberSelected, setMemberSelected] = useState(null);
  const [memberDescription, setMemberDescription] = useState('');

  const [postQ, setPostQ] = useState('');
  const [postResults, setPostResults] = useState([]);
  const [postSelected, setPostSelected] = useState(null);
  const [conversationSummary, setConversationSummary] = useState('');

  const [themeLabel, setThemeLabel] = useState('');
  const [themeCount, setThemeCount] = useState('');

  // Auth gate + load existing
  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/me/is-admin`, { withCredentials: true });
        if (!data.is_admin) {
          setAuthorized(false);
          return;
        }
        setAuthorized(true);
        const { data: pickRes } = await axios.get(`${API}/admin/weekly-picks/current`, { withCredentials: true });
        if (pickRes.pick) {
          if (pickRes.pick.member_user_id) {
            // Fetch the user
            const { data: us } = await axios.get(`${API}/admin/users/search?q=${encodeURIComponent(pickRes.pick.member_user_id)}`, { withCredentials: true });
            const found = (us.users || []).find(u => u.user_id === pickRes.pick.member_user_id);
            if (found) setMemberSelected(found);
          }
          setMemberDescription(pickRes.pick.member_description || '');
          if (pickRes.pick.conversation_post_id) {
            // best-effort: not refetching the post (no public single-post fetch needed here)
            setPostSelected({ post_id: pickRes.pick.conversation_post_id, content: '(post déjà sélectionné)' });
          }
          setConversationSummary(pickRes.pick.conversation_summary || '');
          if (pickRes.pick.theme) {
            setThemeLabel(pickRes.pick.theme.label || '');
            setThemeCount(pickRes.pick.theme.experts_count || '');
          }
        }
      } catch (e) {
        setAuthorized(false);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  // Debounced searches
  useEffect(() => {
    if (!memberQ.trim()) { setMemberResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/admin/users/search?q=${encodeURIComponent(memberQ)}`, { withCredentials: true });
        setMemberResults(data.users || []);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [memberQ]);

  useEffect(() => {
    if (!postQ.trim()) { setPostResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/admin/posts/search?q=${encodeURIComponent(postQ)}`, { withCredentials: true });
        setPostResults(data.posts || []);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [postQ]);

  const publish = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/weekly-picks`, {
        member_user_id: memberSelected?.user_id || null,
        member_description: memberDescription,
        conversation_post_id: postSelected?.post_id || null,
        conversation_summary: conversationSummary,
        theme_label: themeLabel || null,
        theme_experts_count: themeCount ? parseInt(themeCount, 10) : null,
      }, { withCredentials: true });
      toast.success('Sélection publiée pour la semaine en cours');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur publication');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-purple-600" /></div>;
  if (!authorized) {
    return (
      <div className="text-center py-20" data-testid="admin-not-authorized">
        <ShieldAlert className="h-10 w-10 text-red-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold">Accès refusé</h1>
        <p className="text-sm text-gray-500 mt-2">Cette page est réservée aux administrateurs OpenSapiens.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 space-y-6" data-testid="admin-weekly-picks-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Sélection de la semaine</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Cure les 3 items qui apparaîtront sur la home (Zone "Cette semaine"). Si rien n'est publié, un fallback "Tendances" automatique prend le relais.
        </p>
      </div>

      {/* Member spotlight */}
      <section className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70" data-testid="admin-section-member">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-3">1 · Membre à mettre en avant</h2>
        <div className="relative">
          <input
            data-testid="admin-member-search"
            value={memberQ}
            onChange={(e) => setMemberQ(e.target.value)}
            placeholder="Rechercher par nom ou email..."
            className="w-full px-4 py-2.5 pl-10 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-sm"
          />
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>
        {memberResults.length > 0 && (
          <ul className="mt-2 max-h-60 overflow-y-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl divide-y divide-gray-100 dark:divide-gray-700">
            {memberResults.map(u => (
              <li key={u.user_id}>
                <button
                  data-testid={`admin-member-pick-${u.user_id}`}
                  type="button"
                  onClick={() => { setMemberSelected(u); setMemberQ(''); setMemberResults([]); }}
                  className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
                >
                  <strong>{u.display_name}</strong> · {u.email}
                </button>
              </li>
            ))}
          </ul>
        )}
        {memberSelected && (
          <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl text-sm flex items-center justify-between">
            <span><strong>{memberSelected.display_name}</strong> · {memberSelected.email}</span>
            <button onClick={() => setMemberSelected(null)} className="text-xs text-red-600 hover:underline">Retirer</button>
          </div>
        )}
        <textarea
          data-testid="admin-member-description"
          value={memberDescription}
          onChange={(e) => setMemberDescription(e.target.value)}
          rows={2}
          placeholder="Description courte (ex. : Coach inspirant en transitions pro)"
          className="w-full mt-3 px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-sm"
        />
      </section>

      {/* Conversation pick */}
      <section className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70" data-testid="admin-section-conversation">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-3">2 · Conversation à épingler</h2>
        <div className="relative">
          <input
            data-testid="admin-post-search"
            value={postQ}
            onChange={(e) => setPostQ(e.target.value)}
            placeholder="Rechercher dans le contenu des posts..."
            className="w-full px-4 py-2.5 pl-10 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-sm"
          />
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>
        {postResults.length > 0 && (
          <ul className="mt-2 max-h-60 overflow-y-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl divide-y divide-gray-100 dark:divide-gray-700">
            {postResults.map(p => (
              <li key={p.post_id}>
                <button
                  data-testid={`admin-post-pick-${p.post_id}`}
                  type="button"
                  onClick={() => { setPostSelected(p); setPostQ(''); setPostResults([]); }}
                  className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
                >
                  {(p.content || '').slice(0, 120)}…
                </button>
              </li>
            ))}
          </ul>
        )}
        {postSelected && (
          <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl text-sm flex items-start justify-between gap-3">
            <span className="line-clamp-2">{(postSelected.content || '').slice(0, 200)}</span>
            <button onClick={() => setPostSelected(null)} className="text-xs text-red-600 hover:underline shrink-0">Retirer</button>
          </div>
        )}
        <textarea
          data-testid="admin-conversation-summary"
          value={conversationSummary}
          onChange={(e) => setConversationSummary(e.target.value)}
          rows={2}
          placeholder="Résumé de la conversation (optionnel, ex. : N experts ont partagé leur avis)"
          className="w-full mt-3 px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-sm"
        />
      </section>

      {/* Theme */}
      <section className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70" data-testid="admin-section-theme">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-3">3 · Thème de la semaine</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <input
            data-testid="admin-theme-label"
            type="text"
            value={themeLabel}
            onChange={(e) => setThemeLabel(e.target.value)}
            placeholder="Label (ex. Leadership)"
            className="sm:col-span-2 px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-sm"
          />
          <input
            data-testid="admin-theme-count"
            type="number"
            value={themeCount}
            onChange={(e) => setThemeCount(e.target.value)}
            placeholder="Nb d'experts"
            className="px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-sm"
          />
        </div>
      </section>

      <div className="flex justify-end">
        <button
          data-testid="admin-publish-btn"
          onClick={publish}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-xl disabled:opacity-60"
        >
          <Save className="h-4 w-4" /> {saving ? 'Publication...' : 'Publier la sélection de la semaine'}
        </button>
      </div>
    </div>
  );
};

export default AdminWeeklyPicks;
